import type { Booking } from '@/types';

/** The shape of a Bryntum event record, as far as selection needs it. */
export interface SelectedEventLike {
  id?: unknown;
  data?: {
    id?: unknown;
    isAbsenceEvent?: boolean;
    isHeatmapEvent?: boolean;
    isWhatIfGhost?: boolean;
    originalBooking?: Booking;
  };
}

interface ResolveOptions {
  findBooking: (eventId: string) => Booking | undefined;
  /** Ids of occurrences mid-deletion — they must not be deleted again. */
  isLocked?: (bookingId: string) => boolean;
  /** Bookings of a type the user may not change. */
  isRestricted?: (booking: Booking) => boolean;
}

/**
 * The bookings a set of selected timeline events stands for.
 *
 * Only booking tiles count: absence, heatmap and what-if ghost events share the
 * event store but have no booking behind them. A multi-resource booking renders
 * one tile per resource row, so the same booking can arrive several times and is
 * de-duplicated on its own id.
 */
export function bookingsFromSelectedEvents(
  selected: readonly SelectedEventLike[],
  { findBooking, isLocked, isRestricted }: ResolveOptions,
): Booking[] {
  const bookings: Booking[] = [];
  const seen = new Set<string>();

  for (const record of selected) {
    const data = record?.data;
    if (data?.isAbsenceEvent || data?.isHeatmapEvent || data?.isWhatIfGhost) continue;

    const rawId = record?.id ?? data?.id;
    if (rawId == null) continue;

    const booking = findBooking(String(rawId)) ?? data?.originalBooking;
    if (!booking) continue;

    const bookingId = String(booking.id);
    if (seen.has(bookingId)) continue;
    if (isLocked?.(bookingId)) continue;
    if (isRestricted?.(booking)) continue;

    seen.add(bookingId);
    bookings.push(booking);
  }

  return bookings;
}

/**
 * What a bulk action on `clicked` should target.
 *
 * A right-click inside a multi-selection acts on the whole selection; a
 * right-click on anything else acts on that booking alone, leaving the
 * selection untouched. This is shared by bulk delete and status changes.
 */
export function bulkActionTargets(clicked: Booking, selection: readonly Booking[]): Booking[] {
  const clickedId = String(clicked.id);
  const isInSelection = selection.some(booking => String(booking.id) === clickedId);
  return selection.length > 1 && isInSelection ? [...selection] : [clicked];
}
